import Config from 'react-native-config';
import { RelatedDataTypes } from 'serverTypes/event';
import { objectToQsParam } from 'modules/utils/UrlUtils';

/**
 * DeepLinks should contain a route for mobile and a path for web
 */
export interface DeepLinkRouteDefinition {
  route: string;
  path: string;
  routeName?: string;
  params?: Record<any, any>;
  amwell?: boolean;
}

type LinkGeneraterFunction = (
  id?: number | string,
  relatedData?: RelatedDataTypes
) => DeepLinkRouteDefinition;

const getMedicationRouteLink: LinkGeneraterFunction = (id, relatedData) => {
  if (Number(id) > 0) {
    let path = `/u/health-record/current-medications/medications/${id}`;
    if (relatedData?.mhpDetails) {
      path = `/u/health-record/current-medications/medications/${id}/${objectToQsParam(
        relatedData.mhpDetails
      )}`;
    }
    return {
      routeName: 'MedicationDetailsSummary',
      path,
      route: `healthRecords/medications/${id}`,
      params: {
        id,
        mhpDetails: relatedData?.mhpDetails || {}
      }
    };
  }

  return {
    routeName: 'MedicationsScreen',
    route: `healthRecords/medications`,
    path: `/u/health-record/current-medications/medications`,
    params: { medicationType: 'current' }
  };
};

const getVisitSummaryRouteLink: LinkGeneraterFunction = (id, relatedData) => {
  if (Number(id) > 0) {
    let path = `/u/health-record/visit-summaries/live/fhir/${id}`;
    if (Config.VISIT_SUMMARY_EXPANSION_ENABLED && relatedData?.mhpDetails) {
      path = `/u/health-record/visit-summaries/live-detailed/millenium/${id}/${objectToQsParam(
        relatedData.mhpDetails
      )}`;
    }
    return {
      routeName: 'VisitSummaryLive',
      path,
      route: `healthRecords/visitSummary/fhir/${id}`,
      params: {
        source: 'fhir',
        id,
        mhpDetails: relatedData?.mhpDetails
      }
    };
  }

  return {
    routeName: 'VisitSummarySearch',
    path: '/u/health-record/visit-summaries',
    route: `healthRecords/visitSummary`
  };
};

const getGoalsRouteLink: LinkGeneraterFunction = () => ({
  route: `healthRecords/home/goals`,
  path: '/u/health-record',
  params: {
    expand: 'goals'
  }
});

const getUpcomingAppointmentRouteLink: LinkGeneraterFunction = id => ({
  route: `getCare/booking/upcoming/${id}`,
  path: '/u/appointments'
});

const getVideoVisitRouteLink: LinkGeneraterFunction = () => ({
  amwell: true,
  path: '/u/get-care-now/connect-care',
  route: 'getCare/telehealth/landing'
});

const getMessagingRouteLink: LinkGeneraterFunction = () => ({
  route: 'messaging/home',
  path: '/u/messages'
});

const getNewBillRouteLink: LinkGeneraterFunction = id => ({
  route: `bill/detail/${id}`,
  path: `/u/billing/bill-detail/${id}`
});

const getUnpaidBillRouteLink: LinkGeneraterFunction = () => ({
  route: 'bill/unpaid',
  path: '/u/billing/bills'
});

const getDashboardRouteLink: LinkGeneraterFunction = (id, additionalParams) => ({
  route: 'home/root/dashboard',
  path: '/u/dashboard',
  params: {
    category: 'carousel',
    ...additionalParams
  }
});

const getTestResultsRouteLink: LinkGeneraterFunction = () => ({
  route: 'healthRecords/testResultList',
  path: '/u/health-record/test-results',
  routeName: 'TestResultsHealthRecordsList'
});

const getDefaultRouteLink: LinkGeneraterFunction = () => ({
  route: 'home/root/dashboard',
  path: '/u/dashboard'
});

const getVisitPayRouteLink: LinkGeneraterFunction = () => ({
  route: 'bill',
  path: '/u/billing/bills'
});

const getRegistrationCallbackLink = () => ({
  complete: 'register:complete',
  cancel: 'register:cancel'
});

const getAuthCallbackLink = () => ({
  close: 'auth:close'
});

export const RouteLinkGenerator = {
  medication: getMedicationRouteLink,
  visitSummary: getVisitSummaryRouteLink,
  goals: getGoalsRouteLink,
  upcomingAppointment: getUpcomingAppointmentRouteLink,
  videoVisit: getVideoVisitRouteLink,
  messaging: getMessagingRouteLink,
  newBill: getNewBillRouteLink,
  unpaidBill: getUnpaidBillRouteLink,
  dashboard: getDashboardRouteLink,
  testResults: getTestResultsRouteLink,
  default: getDefaultRouteLink,
  visitPay: getVisitPayRouteLink,
  registrationCallback: getRegistrationCallbackLink,
  authCallback: getAuthCallbackLink
};
